import css from './autoFit.module.less';
import c from 'classnames';
import { HTMLProps, useLayoutEffect, useRef, useState } from 'react';

export interface AutoFitProps extends HTMLProps<HTMLDivElement> {
    disableRotate?: boolean;
    /** Content is held upright by an ancestor's counter-rotation, so fit against the on-screen box. */
    upright?: boolean;
    align?: 'bottom' | 'top' | 'left' | 'right' | 'center';
    hideUntilReady?: boolean;
    /** Search for a layout width whose text wrapping leaves room for a larger scale. */
    optimizeWrap?: boolean;
}

// Every probe forces a layout, so keep them few: the widths are coarse but the winner is within a few percent either way.
const wrapProbes = 6;

/**
 * At exactly the container width, content breaks where the *unscaled* text runs out of room, which leaves ragged lines and
 * therefore more height than needed. Probe wider layout widths instead and keep whichever one scores best.
 */
function applyBestWrapWidth(content: HTMLElement, fitWidth: number, score: (width: number, height: number) => number) {
    if (!fitWidth) return;

    function measure(width: string) {
        content.style.width = width;
        return { width: content.offsetWidth, height: content.offsetHeight };
    }

    const natural = measure('max-content');
    if (natural.width <= fitWidth) {
        content.style.width = '';
        return;
    }

    let bestWidth = fitWidth;
    let bestScore = -Infinity;

    for (let i = 0; i <= wrapProbes; i++) {
        // Geometric, so the ends of the range are the container width and no wrapping at all.
        const probeWidth = Math.round(fitWidth * (natural.width / fitWidth) ** (i / wrapProbes));
        const probe = measure(`${probeWidth}px`);
        const probeScore = score(probe.width, probe.height);

        if (probeScore > bestScore) {
            bestScore = probeScore;
            bestWidth = probeWidth;
        }
    }

    content.style.width = `${bestWidth}px`;
}

function readsSwapped(element: HTMLElement, width: number, height: number): boolean {
    if (!width || !height || width === height) return false;

    const { width: screenWidth, height: screenHeight } = element.getBoundingClientRect();
    if (!screenWidth || !screenHeight) return false;

    const onScreen = screenWidth / screenHeight;
    return Math.abs(onScreen - height / width) < Math.abs(onScreen - width / height);
}

export function AutoFit({ disableRotate, upright, align, children, hideUntilReady, optimizeWrap, ...props }: AutoFitProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [rotate, setRotate] = useState(0);
    const [scale, setScale] = useState(1);
    const [animate, setAnimate] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const updateRef = useRef<() => void>(undefined);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const content = contentRef.current;

        if (!container || !content) return;

        let lastWrapKey: string | undefined;

        let applied: string | undefined;

        const apply = (nextScale: number, nextRotate: number) => {
            // Writing invalidates layout, so skip it while nothing changes: this runs on every render.
            const next = `${nextScale}|${nextRotate}`;
            if (next === applied) return;
            applied = next;

            // Straight to the DOM as well, so a content change cannot be painted at the previous scale.
            container.style.setProperty('--scale', `${nextScale}`);
            container.style.setProperty('--rotate', `${nextRotate}deg`);
            setScale(nextScale);
            setRotate(nextRotate);
        };

        const update = () => {
            const parent = container.parentElement;
            if (!parent) {
                apply(1, 0);
                return;
            }
            const width = container.offsetWidth;
            const height = container.offsetHeight;

            const swapped = !!upright && readsSwapped(container, width, height);
            const fitWidth = swapped ? height : width;
            const fitHeight = swapped ? width : height;

            const wrapKey = `${fitWidth}x${fitHeight}|${document.fonts?.status}|${content.textContent}`;
            if (optimizeWrap && wrapKey !== lastWrapKey) {
                lastWrapKey = wrapKey;
                applyBestWrapWidth(content, fitWidth, (contentWidth, contentHeight) => {
                    const straight = Math.min(fitWidth / contentWidth, fitHeight / contentHeight);
                    const rotated = Math.min(fitHeight / contentWidth, fitWidth / contentHeight);
                    return Math.min(1, disableRotate ? straight : Math.max(straight, rotated));
                });
            }

            const contentWidth = content.offsetWidth + Number(content.style.marginLeft) + Number(content.style.marginRight);
            const contentHeight = content.offsetHeight + Number(content.style.marginTop) + Number(content.style.marginBottom);

            if (contentWidth <= fitWidth && contentHeight <= fitHeight) {
                apply(1, 0);
            } else {
                const scale = Math.min(fitWidth / contentWidth, fitHeight / contentHeight);
                const scaleRotated = Math.min(fitHeight / contentWidth, fitWidth / contentHeight);

                if (scale >= scaleRotated || disableRotate) {
                    apply(scale, 0);
                } else {
                    apply(scaleRotated, -90);
                }
            }

            if (!initialized && hideUntilReady) {
                setTimeout(() => {
                    setInitialized(true);
                });
            }
        };

        updateRef.current = update;

        const ro = new ResizeObserver(update);
        ro.observe(container);
        ro.observe(content);

        const timeout = setTimeout(() => {
            setAnimate(true);
        });

        return () => {
            clearTimeout(timeout);
            ro.disconnect();
            content.style.width = '';
        };
    }, [containerRef.current, contentRef.current, disableRotate, upright, hideUntilReady, optimizeWrap]);

    // Re-fit before the browser paints, so changed content is never shown at the scale of the previous content.
    useLayoutEffect(() => {
        updateRef.current?.();
    });

    return (
        <div
            {...props}
            ref={containerRef}
            className={c(props.className, css.fit, css[align ?? ''], { [css.optimizeWrap]: optimizeWrap })}
            style={
                {
                    ...props.style,
                    '--rotate': `${rotate}deg`,
                    '--scale': scale,
                    opacity: !hideUntilReady || initialized ? 1 : 0,
                    transition: `opacity 0.1s`,
                } as any
            }
        >
            <div ref={contentRef} className={c(css.content, css[align ?? ''], { [css.optimizeWrap]: optimizeWrap })}>
                <div className={c(css.rotationWrapper, { [css.animate]: animate })}>{children}</div>
            </div>
        </div>
    );
}
